/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ActivityType, ActivityTypeDTO } from 'src/app/_model/activity-type';
import { ActivityTypesDaoService } from '../../dao/activity-types-dao.service';
import { ActivityTypesService } from '../activity-types.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class ActivityTypesLogicService implements ActivityTypesService  {
  public activityTypes$ = new BehaviorSubject<ActivityType[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private activityTypeDaoService: ActivityTypesDaoService,
    private authenticationService: AuthenticationService
  ) {
    this.initialize();
  }

  // Public Functions

  public getActivityTypeById(id: IMSId): ActivityType {
    if (id === undefined) {
      return undefined
    }

    return this.activityTypes$.getValue().find((at) => {
      if (at.id.imsId == id.imsId && at.id.secondaryId == id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    await this.activityTypeDaoService.reset();
  }

  public async clear() {
    await this.activityTypeDaoService.clear();
    this.activityTypes$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void>{
    await this.clear();
    await this.loadRemoteActivityTypes(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalActivityTypes();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalActivityTypes() {
    try {
      this.logger.log(this.constructor.name, '- Load local ActivityTypes');
      let activityTypes: ActivityType[] = await this.activityTypeDaoService.getActivityTypes();

      this.activityTypes$.next(activityTypes);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local ActivityTypes: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteActivityTypes(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote ActivityTypes from IMS'
      );

      try {
        let activityTypes: ActivityType[] = [];
        let activityTypesDto: ActivityTypeDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncactivitytypes',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        this.logger.log(
          this.constructor.name,
          'Downloaded ActivityTypesDto: ',
          activityTypesDto
        );

        for (const activityTypeDto of activityTypesDto) {
          activityTypes.push(ActivityType.fromDto(activityTypeDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded ActivityTypes: ',
          activityTypes
        );

        await this.activityTypeDaoService.saveActivityTypes(activityTypes);
        await this.activityTypes$.next(activityTypes);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading ActivityTypes: ',
          error?.message
        );
        throw error;
      }
    } else {
      let error = new Error(
        'Cannot download Worktypes - user not authenticated'
      );
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }
}
